// Simple extraction of chart rendering functions - no changes to logic
import { createStripedPattern } from "./chartUtils";

export const renderGlobalIndexChart = (chartInstance, userData, allClientsData, handleChartLevelChange, setChartHistory) => {
  const chart = chartInstance.current;

  const clientMap = Object.keys(allClientsData)
    .map((key) => ({
      company: key,
      indiceSinteticoGlobale:
        allClientsData[key]?.["Indice Sintetico Globale"] || 0,
      hasIncompleteData: allClientsData[key]?.incompleted_data === true,
    }))
    .filter((c) => c.indiceSinteticoGlobale !== undefined);

  clientMap.sort(
    (a, b) =>
      parseFloat(b.indiceSinteticoGlobale) -
      parseFloat(a.indiceSinteticoGlobale)
  );
  var average =
    clientMap
      .filter((c) => !c.hasIncompleteData && c.indiceSinteticoGlobale > 0)
      .reduce((sum, c) => sum + parseFloat(c.indiceSinteticoGlobale), 0) /
    clientMap.filter(
      (c) => !c.hasIncompleteData && c.indiceSinteticoGlobale > 0
    ).length;
  chart.setOption({
    title: { text: "Punteggio globale aziende", left: "center" },
    xAxis: {
      type: "category",
      data: clientMap.map((c) =>
        c.company === userData?.company ? c.company : "-"
      ),
      axisLabel: {
        rotate: 45,
        fontSize: 9,
      },
    },
    yAxis: { type: "value" },
    series: [
      {
        type: "bar",
        data: clientMap.map((c) => ({
          value: parseFloat(c.indiceSinteticoGlobale) || 0,
          company: c.company,
          hasIncompleteData: c.hasIncompleteData,
        })),
        itemStyle: {
          color: (params) => {
            const c = params.data.company;
            const baseColor = userData?.company === c ? "#fc6d00" : "#b5b4b3";

            // If company has incomplete data, add striped pattern
            if (params.data.hasIncompleteData) {
              return {
                type: "pattern",
                image: createStripedPattern(baseColor),
                repeat: "repeat",
              };
            }

            return baseColor;
          },
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            if (params.data.company === userData.company) {
              const suffix = params.data.hasIncompleteData ? "*" : "";
              return params.value.toFixed(2) + suffix;
            }
            return "";
          },
          fontSize: 9,
        },
      },
      {
        name: "Media",
        type: "line",
        data: clientMap.map((c) => average),
        smooth: 0.6,
        symbol: "none",
        lineStyle: {
          type: "dashed",
          color: "green",
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            return "Media: " + average.toFixed(2);
          },
          fontSize: 12,
          color: "green",
          fontWeight: "bold",
        },
      },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        const data = params[0].data;
        const incompleteNote = data.hasIncompleteData
          ? " (Non confrontabile)"
          : "";
        return `Indice: ${data.value.toFixed(2)}${incompleteNote}`;
      },
    },
  });

  chart.off("click");
  chart.on("click", (params) => {
    const clickedCompany = params.data.company;
    if (clickedCompany === userData?.company) {
      handleChartLevelChange(1);
      setChartHistory((prev) => [...prev, { level: 0, title: "" }]);
    }
  });
};

export const renderCitiesChart = (chartInstance, userData, allClientsData, handleChartLevelChange, setChartHistory) => {
  const chart = chartInstance.current;

  const allCitiesData = [];
  Object.keys(allClientsData).forEach((company) => {
    const companyData = allClientsData[company];
    const hasIncompleteData = companyData.incompleted_data === true;

    if (companyData.Citta) {
      Object.keys(companyData.Citta).forEach((city) => {
        const cityData = companyData.Citta[city];
        const indiceSintetico = cityData["Indice Sintetico Citta"] || 0;

        allCitiesData.push({
          company,
          city,
          score: parseFloat(indiceSintetico),
          isUserCity: company === userData?.company,
          hasIncompleteData: hasIncompleteData,
        });
      });
    }
  });

  allCitiesData.sort((a, b) => b.score - a.score);

  const chartSeriesData = [];
  const chartLabels = [];

  allCitiesData.forEach((cityInfo) => {
    const baseColor = cityInfo.isUserCity ? "#fc6d00" : "#b5b4b3";

    chartSeriesData.push({
      value: cityInfo.score,
      itemStyle: {
        color: cityInfo.hasIncompleteData
          ? {
              type: "pattern",
              image: createStripedPattern(baseColor),
              repeat: "repeat",
            }
          : baseColor,
      },
      originalCompany: cityInfo.company,
      originalCity: cityInfo.city,
      isUserCity: cityInfo.isUserCity,
      hasIncompleteData: cityInfo.hasIncompleteData,
    });

    if (cityInfo.isUserCity) {
      const incompleteMarker = cityInfo.hasIncompleteData ? "*" : "";
      chartLabels.push(
        `${cityInfo.city}\n(${cityInfo.company})${incompleteMarker}`
      );
    } else {
      chartLabels.push("");
    }
  });
  var average =
    chartSeriesData
      .filter((c) => !c.hasIncompleteData && c.value > 0)
      .reduce((sum, city) => sum + city.value, 0) /
    chartSeriesData.filter((c) => !c.hasIncompleteData && c.value > 0).length;
  chart.setOption({
    title: {
      text: "Punteggio globale servizi fisici su territorio",
      left: "center",
    },
    xAxis: {
      type: "category",
      data: chartLabels,
      axisLabel: {
        fontSize: 9,
        rotate: 50,
        formatter: (value) =>
          value.length > 20 ? value.slice(0, 20) + "…" : value,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
          color: "#e8e8e8",
        },
      },
    },
    yAxis: {
      type: "value",
      name: "Indice Sintetico Citta",
      nameLocation: "middle",
      nameGap: 50,
    },
    series: [
      {
        type: "bar",
        data: chartSeriesData,
        barWidth: "60%",
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            const data = params.data;
            if (!data.isUserCity) return "";
            const suffix = data.hasIncompleteData ? "*" : "";
            return data.value.toFixed(2) + suffix;
          },
          fontSize: 9,
        },
      },
      {
        type: "line",
        data: chartSeriesData.map((c) => average),
        smooth: 0.6,
        symbol: "none",
        lineStyle: {
          type: "dashed",
          color: "green",
          width: 2,
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            return `Media: ${params.data.toFixed(2)}`;
          },
          fontSize: 12,
          color: "green",
          fontWeight: "bold",
        },
      },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        const data = params[0].data;
        const incompleteNote = data.hasIncompleteData
          ? " (Non confrontabile)"
          : "";
        return `${data.value.toFixed(2)}${incompleteNote}`;
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "20%",
      containLabel: true,
    },
  });

  chart.off("click");
  chart.on("click", (params) => {
    const clickedData = params.data;
    if (clickedData.value === null) return;

    if (clickedData.originalCompany === userData?.company) {
      handleChartLevelChange(2);
      setChartHistory((prev) => [
        ...prev,
        {
          level: 1,
          title: ``,
          data: { city: clickedData.originalCity },
        },
      ]);
    }
  });
};

export const renderCallCenterMediaPesataChart = (chartInstance, userData, allClientsData, handleChartLevelChange, setChartHistory) => {
  const chart = chartInstance.current;
  if (!chart) return;

  const callCenterData = Object.keys(allClientsData)
    .map((key) => ({
      company: key,
      mediaPesata:
        allClientsData[key]?.["Call Center"]?.["MEDIA PESATA"] || 0,
    }))
    .filter((d) => d.mediaPesata !== undefined);

  callCenterData.sort(
    (a, b) => parseFloat(b.mediaPesata) - parseFloat(a.mediaPesata)
  );
  var average =
    callCenterData
      .filter((d) => d.mediaPesata > 0)
      .reduce((sum, d) => sum + parseFloat(d.mediaPesata), 0) /
    callCenterData.filter((d) => d.mediaPesata > 0).length;
  chart.setOption({
    title: { text: " Punteggio globale call center", left: "center" },
    xAxis: {
      type: "category",
      data: callCenterData.map((d) =>
        d.company === userData?.company ? d.company : "-"
      ),
      axisLabel: { rotate: 45 },
    },
    yAxis: { type: "value" },
    series: [
      {
        type: "bar",
        data: callCenterData.map((d) => ({
          value: parseFloat(d.mediaPesata) || 0,
          company: d.company,
        })),
        itemStyle: {
          color: (params) =>
            params.data.company === userData?.company ? "#fc6d00" : "#b5b4b3",
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) =>
            params.data.company === userData?.company
              ? params.value.toFixed(2)
              : "",
        },
      },
      {
        type: "line",
        data: callCenterData.map((d) => average),
        symbol: "none",
        lineStyle: {
          type: "dashed",
          color: "green",
          width: 2,
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            return `Media: ${params.data.toFixed(2)}`;
          },
          fontSize: 12,
          color: "green",
          fontWeight: "bold",
        },
      },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        const data = params[0].data;
        return `Call Center - Media Pesata: ${data.value.toFixed(2)}`;
      },
    },
  });

  chart.off("click");
  chart.on("click", (params) => {
    const clickedData = params.data;
    if (clickedData.company === userData?.company) {
      handleChartLevelChange(5);
      setChartHistory((prev) => [
        ...prev,
        {
          level: 1,
          title: " Punteggio globale call center",
          data: { category: "Call Center" },
        },
      ]);
    }
  });
};

export const renderSitoMediaPesataChart = (chartInstance, userData, allClientsData, handleChartLevelChange, setChartHistory) => {
  const chart = chartInstance.current;
  if (!chart) return;

  const sitoData = Object.keys(allClientsData)
    .map((key) => ({
      company: key,
      mediaPesata: allClientsData[key]?.["Sito"]?.["MEDIA PESATA"] || 0,
    }))
    .filter((d) => d.mediaPesata !== undefined);

  sitoData.sort(
    (a, b) => parseFloat(b.mediaPesata) - parseFloat(a.mediaPesata)
  );
  var average =
    sitoData
      .filter((d) => d.mediaPesata > 0)
      .reduce((sum, d) => sum + parseFloat(d.mediaPesata), 0) /
    sitoData.filter((d) => d.mediaPesata > 0).length;
  chart.setOption({
    title: { text: " Punteggio globale sito web", left: "center" },
    xAxis: {
      type: "category",
      data: sitoData.map((d) =>
        d.company === userData?.company ? d.company : "-"
      ),
      axisLabel: { rotate: 45 },
    },
    yAxis: { type: "value" },
    series: [
      {
        type: "bar",
        data: sitoData.map((d) => ({
          value: parseFloat(d.mediaPesata) || 0,
          company: d.company,
        })),
        itemStyle: {
          color: (params) =>
            params.data.company === userData?.company ? "#fc6d00" : "#b5b4b3",
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) =>
            params.data.company === userData?.company
              ? params.value.toFixed(2)
              : "",
        },
      },
      {
        type: "line",
        data: sitoData.map((d) =>
          average ? parseFloat(average).toFixed(2) : null
        ),
        symbol: "none",
        lineStyle: {
          type: "dashed",
          color: "green",
          width: 2,
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            return `Media: ${params.data}`;
          },
          fontSize: 12,
          color: "green",
          fontWeight: "bold",
        },
        markLine: {
          lineStyle: {
            type: "solid",
            color: "#000",
            width: 2.5,
          },
        },
      },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        const data = params[0].data;
        return `Sito - Media Pesata: ${data.value.toFixed(2)}`;
      },
    },
  });

  chart.off("click");
  chart.on("click", (params) => {
    const clickedData = params.data;
    if (clickedData.company === userData?.company) {
      handleChartLevelChange(4);
      setChartHistory((prev) => [
        ...prev,
        {
          level: 1,
          title: " Punteggio globale sito web",
          data: { category: "Sito" },
        },
      ]);
    }
  });
};

export const renderAppMediaPesataChart = (chartInstance, userData, allClientsData, handleChartLevelChange, setChartHistory) => {
  const chart = chartInstance.current;
  if (!chart) return;

  const appData = Object.keys(allClientsData)
    .map((key) => ({
      company: key,
      mediaPesata: allClientsData[key]?.["App"]?.["MEDIA PESATA"] || 0,
    }))
    .filter((d) => d.mediaPesata !== undefined);

  appData.sort(
    (a, b) => parseFloat(b.mediaPesata) - parseFloat(a.mediaPesata)
  );
  var average =
    appData
      .filter((d) => d.mediaPesata > 0)
      .reduce((sum, d) => sum + parseFloat(d.mediaPesata), 0) /
    appData.filter((d) => d.mediaPesata > 0).length;
  chart.setOption({
    title: { text: "Punteggio globale app", left: "center" },
    xAxis: {
      type: "category",
      data: appData.map((d) =>
        d.company === userData?.company ? d.company : "-"
      ),
      axisLabel: { rotate: 45 },
    },
    yAxis: { type: "value" },
    series: [
      {
        type: "bar",
        data: appData.map((d) => ({
          value: parseFloat(d.mediaPesata) || 0,
          company: d.company,
        })),
        itemStyle: {
          color: (params) =>
            params.data.company === userData?.company ? "#fc6d00" : "#b5b4b3",
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) =>
            params.data.company === userData?.company
              ? params.value.toFixed(2)
              : "",
        },
      },
      {
        type: "line",
        data: appData.map((d) =>
          average ? parseFloat(average).toFixed(2) : null
        ),
        symbol: "none",
        lineStyle: {
          type: "dashed",
          color: "green",
          width: 2,
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            return `Media: ${params.data}`;
          },
          fontSize: 12,
          color: "green",
          fontWeight: "bold",
        },
        markLine: {
          lineStyle: {
            type: "solid",
            color: "#000",
            width: 2.5,
          },
        },
      },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        const data = params[0].data;
        return `App - Media Pesata: ${data.value.toFixed(2)}`;
      },
    },
  });

  chart.off("click");
  chart.on("click", (params) => {
    const clickedData = params.data;
    if (clickedData.company === userData?.company) {
      handleChartLevelChange(3);
      setChartHistory((prev) => [
        ...prev,
        {
          level: 1,
          title: "Punteggio globale app",
          data: { category: "App" },
        },
      ]);
    }
  });
};

export const renderCategoryDetailsChart = (chartInstanceToUse, categoryName, subMetrics, userData, allClientsData) => {
  const chart = chartInstanceToUse.current;
  if (!chart) return;

  let chartTitle = "";
  if (categoryName === "App") {
    chartTitle = "Punteggio globale app";
  } else if (categoryName === "Sito") {
    chartTitle = "Punteggio globale sito web";
  } else if (categoryName === "Call Center") {
    chartTitle = "Punteggio globale call center";
  } else {
    chartTitle = `Punteggio categorie ${categoryName.toLowerCase()}`;
  }

  const categoriesToFilter = ["Sito", "Call Center", "App"];
  let filteredSubMetrics = subMetrics;

  if (categoriesToFilter.includes(categoryName)) {
    filteredSubMetrics = subMetrics.filter(
      (metric) => !metric.toLowerCase().includes("media pesata")
    );
  }

  const companies = Object.keys(allClientsData);
  const allSections = [];
  const separatorPositions = [];
  let cumulativeIndex = 0;

  filteredSubMetrics.forEach((metric, idx) => {
    let metricData = companies.map((company) => ({
      company: company,
      value: allClientsData[company]?.[categoryName]?.[metric] || 0,
    }));

    metricData.sort((a, b) => b.value - a.value);

    metricData.forEach((item) => {
      allSections.push({
        metric: metric,
        value: item.value,
        company: item.company,
      });
      cumulativeIndex++;
    });

    if (idx < filteredSubMetrics.length - 1) {
      allSections.push({ metric: "", value: null, company: "" });
      separatorPositions.push(cumulativeIndex - 0.5);
      cumulativeIndex++;
    }
  });

  if (
    allSections.length > 0 &&
    allSections[allSections.length - 1].metric === ""
  ) {
    allSections.pop();
  }

  const xAxisData = allSections.map((item) => {
    if (item.metric === "") return "";
    return item.company === userData?.company ? item.company : "";
  });

  const xAxisTitles = [];
  let currentIndex = 0;

  filteredSubMetrics.forEach((metric, idx) => {
    const count = companies.length;
    const labelPosition = currentIndex + Math.floor(count / 2);
    while (xAxisTitles.length <= currentIndex + count) {
      xAxisTitles.push("");
    }
    xAxisTitles[labelPosition] = metric;
    currentIndex += count + 1;
  });
  var averages = [];
  filteredSubMetrics.forEach((metric) => {
    const metricData = allSections
      .filter((d) => d.metric === metric && d.value > 0)
      .sort((a, b) => b.value - a.value);
    var average =
      metricData.reduce((sum, city) => sum + city.value, 0) /
      metricData.length;
    averages.push({
      average,
      metric: metric,
      dataLength: allSections.filter((d) => d.metric === metric).length,
    });
  });
  var dataAverages = [];
  averages.forEach((avg) => {
    for (let i = 0; i < avg.dataLength; i++) {
      dataAverages.push(avg.average.toFixed(2));
    }
    dataAverages.push(null);
  });

  chart.setOption({
    title: { text: chartTitle, left: "center" },
    xAxis: [
      {
        type: "category",
        data: xAxisData,
        axisLabel: {
          rotate: 50,
          fontSize: 9,
          formatter: function (value) {
            if (!value) return "";
            return value.length > 15 ? value.slice(0, 15) + "…" : value;
          },
          interval: 0,
        },
        axisTick: { show: false },
      },
      {
        type: "category",
        data: xAxisTitles,
        position: "bottom",
        offset: 60,
        axisLabel: {
          fontSize: 11,
          fontWeight: "bold",
          color: "#000",
          interval: 0,
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
    ],
    yAxis: {
      type: "value",
      name: "Valore",
      nameLocation: "middle",
      nameGap: 30,
    },
    series: [
      {
        type: "bar",
        data: allSections.map((item) => ({
          value: item.value,
          company: item.company,
        })),
        itemStyle: {
          color: (params) => {
            if (params.data.value == null) return "transparent";
            return params.data.company === userData?.company
              ? "#fc6d00"
              : "#b5b4b3";
          },
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            if (params.data.value == null) return "";
            if (params.data.company === userData?.company) {
              return params.value.toFixed(2);
            }
            return "";
          },
          fontSize: 9,
        },
        markLine: {
          symbol: "none",
          lineStyle: {
            type: "solid",
            color: "#000",
            width: 1,
          },
          data: separatorPositions.map((pos) => ({
            xAxis: pos,
            label: { show: false },
          })),
        },
      },
      {
        type: "line",
        data: dataAverages.map((avg) =>
          avg ? parseFloat(avg).toFixed(2) : null
        ),
        symbol: "none",
        lineStyle: {
          type: "dashed",
          color: "green",
          width: 2,
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            return `Media: ${params.data}`;
          },
          fontSize: 12,
          color: "green",
          fontWeight: "bold",
        },
        markLine: {
          lineStyle: {
            type: "solid",
            color: "#000",
            width: 2.5,
          },
        },
      },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        const data = params[0].data;
        if (data.value == null) return "";
        return `Valore: ${data.value.toFixed(2)}`;
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "30%",
      containLabel: true,
    },
    legend: { show: false },
  });

  chart.off("click");
};

export const renderNestedDataChart = (chartInstance, userData, allClientsData, chartHistory) => {
  
  const chart = chartInstance.current;
  
  const currentHistory = chartHistory[chartHistory.length - 1];
  
  const { categoryData, category, city } = currentHistory?.data || {};

  if (!categoryData) {
    chart.setOption({
      title: { text: "Nessun dato disponibile", left: "center" },
      series: [{ type: "bar", data: [] }],
    });
    return;
  }

  let chartTitle = "";
  if (category === "Linee.URBANO") {
    chartTitle = "Punteggio categorie servizio urbano";
  } else if (category === "Linee.EXTRAURBANO") {
    chartTitle = "Punteggio categorie servizio extraurbano";
  } else if (category === "Fermate") {
    chartTitle = "Punteggio categorie fermate";
  } else if (category === "Punti Vendita") {
    chartTitle = "Punteggio categorie punti vendita";
  } else {
    chartTitle = "Punteggio categorie relative al territorio";
  }

  const rawData = [];

  Object.keys(allClientsData).forEach((company) => {
    Object.keys(allClientsData[company]?.Citta || {}).forEach((otherCity) => {
      let otherCategoryData = {};

      if (category === "Linee.URBANO") {
        otherCategoryData =
          allClientsData[company]?.Citta?.[otherCity]?.Linee?.URBANO || {};
      } else if (category === "Linee.EXTRAURBANO") {
        otherCategoryData =
          allClientsData[company]?.Citta?.[otherCity]?.Linee?.EXTRAURBANO ||
          {};
      } else if (category === "Fermate") {
        otherCategoryData =
          allClientsData[company]?.Citta?.[otherCity]?.Fermate || {};
      } else if (category === "Punti Vendita") {
        otherCategoryData =
          allClientsData[company]?.Citta?.[otherCity]?.["Punti Vendita"] ||
          {};
      }

      const metrics = Object.keys(otherCategoryData).filter(
        (key) => key !== "MEDIA PESATA"
      );

      metrics.forEach((metric) => {
        rawData.push({
          metric,
          label: `${otherCity}`,
          value: otherCategoryData[metric] || 0,
          isUserCompany: company === userData?.company,
          city: otherCity,
          company: company,
        });
      });
    });
  });

  const groupedAndSorted = {};
  rawData.forEach((item) => {
    if (!groupedAndSorted[item.metric]) {
      groupedAndSorted[item.metric] = [];
    }
    groupedAndSorted[item.metric].push(item);
  });

  Object.keys(groupedAndSorted).forEach((metric) => {
    groupedAndSorted[metric].sort((a, b) => b.value - a.value);
  });

  const chartData = [];
  const xAxisData = [];
  const xAxisTitles = [];
  const separatorPositions = [];
  let currentIndex = 0;

  const metricsList = Object.keys(groupedAndSorted);

  metricsList.forEach((metric, idx) => {
    const items = groupedAndSorted[metric];
    const groupStartIndex = currentIndex;

    items.forEach((item) => {
      chartData.push(item);
      xAxisData.push(item.isUserCompany ? item.city : "");
      currentIndex++;
    });

    if (idx < metricsList.length - 1) {
      chartData.push({
        value: null,
        label: "",
        city: "",
        isUserCompany: false,
      });
      xAxisData.push("");
      separatorPositions.push(currentIndex - 0.5);
      currentIndex++;
    }

    const labelPosition = groupStartIndex + Math.floor(items.length / 2);

    while (xAxisTitles.length <= currentIndex) {
      xAxisTitles.push("");
    }

    xAxisTitles[labelPosition] = metric;
  });

  var averages = [];
  metricsList.forEach((metric) => {
    const metricData = groupedAndSorted[metric]
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
    var average =
      metricData.reduce((sum, city) => sum + city.value, 0) /
      metricData.length;
    averages.push({
      average,
      metric: metric,
      dataLength: groupedAndSorted[metric].length,
    });
  });
  var dataAverages = [];
  averages.forEach((avg) => {
    for (let i = 0; i < avg.dataLength; i++) {
      dataAverages.push(avg.average.toFixed(2));
    }
    dataAverages.push(null);
  });
  chart.setOption({
    title: {
      text: chartTitle,
      left: "center",
      subtextStyle: { fontSize: 12 },
    },
    xAxis: [
      {
        type: "category",
        data: xAxisData,
        axisLabel: {
          fontSize: 9,
          rotate: 50,
          formatter: (value) => {
            if (!value) return "";
            return value.length > 15 ? value.slice(0, 15) + "…" : value;
          },
          interval: 0,
        },
        axisTick: { show: false },
      },
      {
        type: "category",
        data: xAxisTitles,
        position: "bottom",
        offset: 60,
        axisLabel: {
          fontSize: 11,
          fontWeight: "bold",
          color: "#000",
          interval: 0,
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
    ],
    yAxis: {
      type: "value",
      name: "Valore",
      nameLocation: "middle",
      nameGap: 30,
    },
    series: [
      {
        type: "bar",
        data: chartData,
        itemStyle: {
          color: (params) => {
            if (!params.data || params.data.value == null)
              return "transparent";
            return params.data.city === city && params.data.isUserCompany
              ? "#fc6d00"
              : params.data.isUserCompany
              ? "#f7b17c"
              : "#b5b4b3";
          },
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            if (
              !params.data ||
              params.data.value == null ||
              params.data.city != city
            )
              return "";
            if (params.data.isUserCompany) {
              return params.data.value.toFixed(2);
            }
            return "";
          },
          fontSize: 9,
          color: "#333",
        },
        markLine: {
          symbol: "none",
          lineStyle: {
            type: "solid",
            color: "#000",
            width: 1,
          },

          data: separatorPositions.map((pos) => ({
            xAxis: pos,
            label: { show: false },
          })),
        },
      },
      {
        type: "line",
        data: dataAverages.map((avg) =>
          avg ? parseFloat(avg).toFixed(2) : null
        ),
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            return `Media: ${params.data}`;
          },
          fontSize: 12,
          color: "green",
          fontWeight: "bold",
        },
        symbol: "none",
        lineStyle: {
          type: "dashed",
          color: "green",
          width: 2,
        },
      },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        if (!params || params.length === 0) return "";

        const param = params[0];
        if (
          !param.data ||
          param.data.value == null ||
          isNaN(param.data.value)
        ) {
          return "";
        }

        const cityInfo = param.data.city
          ? `<br/>Città: ${param.data.city}`
          : "";
        const companyInfo = param.data.company
          ? `<br/>Azienda: ${param.data.company}`
          : "";

        return `Valore: ${param.data.value.toFixed(2)}`;
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "30%",
      containLabel: true,
    },
    legend: { show: false },
    animation: true,
    animationDuration: 1000,
    animationEasing: "cubicInOut",
  });

  chart.off("click");
};

export const renderCityDetailsChart = (chartInstance, userData, allClientsData, chartHistory, handleChartLevelChange, setChartHistory) => {
  const chart = chartInstance.current;
  const currentHistory = chartHistory[chartHistory.length - 1];
  const cityName = currentHistory?.data?.city;

  const allTargetMetrics = [
    { key: "Linee.URBANO", label: "Linee - Urbano" },
    { key: "Linee.EXTRAURBANO", label: "Linee - Extraurbano" },
    { key: "Fermate", label: "Fermate" },
    { key: "Punti Vendita", label: "Punti Vendita" },
  ];

  const availableMetrics = [];

  Object.keys(allClientsData).forEach((company) => {
    const companyData = allClientsData[company];
    const cityData = companyData?.Citta?.[cityName];

    if (cityData) {
      allTargetMetrics.forEach((metric) => {
        let hasData = false;

        if (metric.key.startsWith("Linee.")) {
          const lineType = metric.key.split(".")[1];
          const lineData = cityData["Linee"]?.[lineType];
          hasData = lineData && Object.keys(lineData).length > 0;
        } else {
          const categoryData = cityData[metric.key];
          hasData = categoryData && Object.keys(categoryData).length > 0;
        }

        if (hasData && !availableMetrics.some((m) => m.key === metric.key)) {
          availableMetrics.push(metric);
        }
      });
    }
  });

  if (availableMetrics.length === 0) {
    chart.setOption({
      title: {
        text: `Nessun dato disponibile per ${cityName}`,
        left: "center",
      },
      xAxis: { type: "category", data: [] },
      yAxis: { type: "value" },
      series: [{ type: "bar", data: [] }],
    });
    return;
  }

  const targetMetrics = availableMetrics;

  const allDataPoints = [];

  Object.keys(allClientsData).forEach((company) => {
    const companyData = allClientsData[company];
    Object.keys(companyData.Citta || {}).forEach((city) => {
      const cityData = companyData?.Citta?.[city] || {};
      let cityHasData = false;

      targetMetrics.forEach((metric) => {
        let value = 0;
        if (metric.key.startsWith("Linee.")) {
          const lineType = metric.key.split(".")[1];
          value = cityData["Linee"]?.[lineType]?.["MEDIA PESATA"] || 0;
        } else {
          value = cityData[metric.key]?.["MEDIA PESATA"] || 0;
        }

        if (value > 0) cityHasData = true;

        allDataPoints.push({
          company,
          city,
          metric: metric.label,
          value,
          isUserCompany: company === userData?.company,
        });
      });

      if (!cityHasData) {
        for (let i = allDataPoints.length - 1; i >= 0; i--) {
          if (allDataPoints[i].city === city) {
            allDataPoints.splice(i, 1);
          }
        }
      }
    });
  });

  const xAxisData = [];
  const xAxisTitles = [];
  const seriesData = [];
  const colors = [];
  const groupEndPositions = [];
  let cumulativeIndex = 0;

  const userCompanyPositions = [];
  let currentIndex = 0;

  targetMetrics.forEach((metric, metricIndex) => {
    const metricData = allDataPoints
      .filter((d) => d.metric === metric.label)
      .sort((a, b) => b.value - a.value);

    const groupLength = metricData.length;
    const groupStartIndex = currentIndex;

    metricData.forEach((item, index) => {
      xAxisData.push(item.city);
      seriesData.push({
        value: item.value,
        company: item.company,
        metric: item.metric,
        city: item.city,
        isUserCompany: item.isUserCompany,
        isSerie: true,
      });

      if (item.isUserCompany && item.value != 0) {
        userCompanyPositions.push(currentIndex);
      }

      colors.push(
        item.city !== cityName && item.isUserCompany
          ? "#f7b17c"
          : item.isUserCompany
          ? "#fc6d00"
          : "#b5b4b3"
      );
      currentIndex++;
    });

    cumulativeIndex += groupLength;

    if (metricIndex < targetMetrics.length - 1) {
      xAxisData.push("");
      seriesData.push({ value: null });
      colors.push("transparent");
      groupEndPositions.push(cumulativeIndex - 0.5);
      cumulativeIndex += 1;
      currentIndex++;
    }

    const labelPosition = groupStartIndex + Math.floor(groupLength / 2);

    while (xAxisTitles.length <= currentIndex) {
      xAxisTitles.push("");
    }

    xAxisTitles[labelPosition] = metric.label;
  });
  var averages = [];
  targetMetrics.forEach((metric) => {
    const metricData = seriesData
      .filter((d) => d.metric === metric.label && d.value > 0)
      .sort((a, b) => b.value - a.value);
    var average =
      metricData.reduce((sum, city) => sum + city.value, 0) /
      metricData.length;
    averages.push({
      average,
      metric: metric.key,
      dataLength: seriesData.filter((d) => d.metric === metric.label).length,
    });
  });
  var dataAverages = [];
  averages.forEach((avg) => {
    for (let i = 0; i < avg.dataLength; i++) {
      dataAverages.push(avg.average.toFixed(2));
    }
    dataAverages.push(null);
  });

  chart.setOption({
    title: {
      text: `Punteggio categorie relative al territorio`,
      left: "center",
      subtextStyle: { fontSize: 11 },
    },
    xAxis: [
      {
        type: "category",
        data: xAxisData,
        axisLabel: {
          fontSize: 9,
          rotate: 60,
          formatter: (value, index) => {
            return userCompanyPositions.includes(index) ? value : "";
          },
        },
        axisTick: { show: false },
      },
      {
        type: "category",
        data: xAxisTitles,
        position: "bottom",
        offset: 80,
        axisLabel: {
          fontSize: 14,
          fontWeight: "bold",
          color: "#000",
          interval: 0,
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
    ],
    yAxis: {
      type: "value",
      name: "Media Pesata",
      nameLocation: "middle",
      nameGap: 40,
    },
    series: [
      {
        type: "bar",
        data: seriesData,
        itemStyle: {
          color: (params) => colors[params.dataIndex],
        },
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            if (
              !params.data ||
              !params.data.isUserCompany ||
              params.data.value == null ||
              params.data.city != cityName
            )
              return "";
            return `${params.data.value.toFixed(2)}`;
          },
          fontSize: 8,
          color: "grey",
          align: "center",
        },
        markLine: {
          symbol: "none",
          lineStyle: {
            type: "solid",
            color: "#000",
            width: 1,
          },
          data: [
            ...groupEndPositions.map((pos) => ({
              xAxis: pos,
              label: { show: false },
            })),
          ],
        },
      },
      {
        name: "Average",
        type: "line",
        data: dataAverages.map((avg) => avg),
        label: {
          show: true,
          position: "top",
          formatter: (params) => {
            return `Media: ${params.data}`;
          },
          fontSize: 12,
          color: "green",
          fontWeight: "bold",
        },
        emphasis: {
          focus: "series",
        },
        smooth: 0.6,
        symbol: "none",
        lineStyle: {
          type: "dashed",
          color: "green",
          width: 2,
        },
      },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        const data = params[0].data;
        if (!data || data.value == null) return "";
        if (data.company !== userData?.company) return data.value.toFixed(2);
        return `${data.metric}<br/>${data.company}: ${data.value.toFixed(2)}`;
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "25%",
      containLabel: true,
    },
    legend: { show: false },
  });

  chart.off("click");
  chart.on("click", (params) => {
    if (params.componentType !== "series") return;

    const clickedCategory = targetMetrics.find((m) =>
      params?.data?.metric?.startsWith(m.label)
    )?.key;
    const city = currentHistory?.data?.city;
    const company = params.data.company;

    if (company !== userData?.company) return;

    let categoryData = {};
    const companyData = allClientsData[company]?.Citta?.[city];

    if (clickedCategory === "Linee.URBANO") {
      categoryData = companyData?.Linee?.URBANO || {};
    } else if (clickedCategory === "Linee.EXTRAURBANO") {
      categoryData = companyData?.Linee?.EXTRAURBANO || {};
    } else if (clickedCategory === "Fermate") {
      categoryData = companyData?.Fermate || {};
    } else if (clickedCategory === "Punti Vendita") {
      categoryData = companyData?.["Punti Vendita"] || {};
    }

    setChartHistory((prev) => [
      ...prev,
      {
        level: 2,
        title: ``,
        data: { city, category: clickedCategory, categoryData },
      },
    ]);
    handleChartLevelChange(6);
  });
};